import { useState, useEffect, useContext, useRef, useMemo } from "react";

//Components
import Person from "./Person";
import RespondingOfficer from "./RespondingOfficer/RespondingOfficer";
import {
  FormControl,
  TextField,
  Button,
  FormGroup,
  Typography,
  Box,
  TextareaAutosize,
  FormLabel,
  Paper,
  IconButton,
} from "@material-ui/core";
import SingleDropDown from "./SingleDropDown";
import CheckboxWithLabel from "./CheckboxWithLabel";
import MRI from "./MRI";
import LinkedIncidents from "./LinkedIncidents";
import LinkIncident from "./LinkIncident";
import CallLogs from "./CallLogs";
import MultiDropDownObjects from "./MultiDropDownObjects";
import AddressWithAlertAndHmi from "./AddressWithAlertAndHmi";
import CustomAutoComplete from "./CustomAutoComplete";

//Context
import { UserContext } from "../Contexts/UserContext";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { SetupContext } from "../Contexts/SetupContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { DashboardContext } from "../Contexts/DashboardContext";
import { ACTIVE_INCIDENTS_ACTIONS } from "../Actions/ActiveIncidentsActions";
import { MutationContext } from "../Contexts/MutationContext";

//Styling
import { useStyles } from "../Styles/styles";
import clsx from "clsx";

//Utils
import {
  parseObjectArrayIdString,
  handleUuid,
  createHazardTextArray,
  formatOfficerName,
  localGetData,
  parseDateFlexible,
  pickPreferredCommonCallType,
} from "../utils/utils";
import {
  createEmptyPersonIncidentSpecific,
  emptyOis,
  emptyNicEntry,
} from "../utils/EmptyObjects";
import { useCookies } from "react-cookie";
import { shouldBeLocked } from "../utils/Roles";

//sound
import { isSuperUser } from "../utils/Roles";

//icons
import ExpandMore from "@material-ui/icons/ExpandMore";
import ExpandLess from "@material-ui/icons/ExpandLess";
import addButton from "../Assets/addButton.png";
import WrappedKeyboardDateTimePicker from "./WrappedKeyboardDateTimePicker";
import { throttleFunction } from "../utils/throttleFunction";
import {
  useHandleAddPersonIncidentSpecific,
  useHandleRemovePerson,
} from "./PersonDetail/hooks";
import NarrativeSegmentsContainer from "./NarrativeSegments/components/NarrativeSegmentsContainer";
import PoldFields from "./PoldFields";

import moment from "moment";

const InitialCallInformation = ({
  incident,
  incidentId,
  incidentIndex,
  disabled = undefined,
  userRoles,
  handleIncidentOpening,
  handlePersonOpening,
  setPausePolling,
  _initialCallInfoIsMounted,
  setPopper,
  getPissForIncident,
  memoizedIsOnIncident,
  incidentPeople,
  handleNarrativeSegmentSubscription = () => {},
  subscriptionServer = null,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles],
  );
  const classes = useStyles();
  const { user, onlineState } = useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { UPDATE_INCIDENT, ADD_PIS, ADD_EMPTY_OIS, ADD_NIC } = INCIDENT_ACTIONS;
  const { activeIncidentDispatch } = useContext(DashboardContext);
  const { UPDATE_ACTIVE_INCIDENT_PROPERTY, ADD_OIS_TO_INCIDENT } =
    ACTIVE_INCIDENTS_ACTIONS;
  const { setup, setupLoading } = useContext(SetupContext);

  const setupLists = useMemo(() => {
    if (setupLoading) return {};
    return setup?.setupLists ?? {};
  }, [setupLoading, setup?.setupLists]);

  const {
    commonCallTypes = [],
    drugTypes = [],
    stateWideEvents: stateWideEventsOptions = [],
    callPriorities = [],
  } = setupLists;

  const specificSetupLists = useMemo(() => {
    if (setupLoading) return {};
    const localStoreSpecificSetup =
      localGetData(`specific-setup-${incident?.ori?.id}`) || {};
    return localStoreSpecificSetup?.setupLists ?? {};
  }, [setupLoading, incident?.ori?.id]);

  const {
    incidentPriorities = [],
    classifications = [],
    incidentOrigins = [],
    areas = [],
    teams = [],
    incidentInvolvements = [],
    incidentFlags = [],
  } = specificSetupLists;

  const [queryReturnHeight, setQueryReturnHeight] = useState("30vh");
  const [showLinkedIncidents, setShowLinkedIncidents] = useState(false);
  const [showLinkIncident, setShowLinkIncident] = useState(false);
  const [showCallLogs, setShowCallLogs] = useState(false);
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const [hazardAlerts, setHazardAlerts] = useState();
  const [cookies] = useCookies();
  const createdIncident =
    incident?.employee?.id?.toString() === user?.id?.toString();

  useEffect(() => {
    _initialCallInfoIsMounted.current = true;
    return () => {
      // ComponentWillUnmount in Class Component
      _initialCallInfoIsMounted.current = false;
    };
  }, [_initialCallInfoIsMounted]);

  useEffect(() => {
    let alerts = createHazardTextArray(incident?.personIncidentSpecifics, "pis")
      .concat(createHazardTextArray(incident?.address, "address"))
      .join(" ");
    setHazardAlerts(alerts);
    if (alerts.length > 1) {
      // console.log('play sound')
      // new Audio(warning2).play();
    }
  }, [incident?.personIncidentSpecifics, incident?.address]);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  const handleSetQueryReturn = () => {
    if (incident.newQueryReturns !== "") {
      setQueryReturns({
        variables: {
          input: {
            incidentId: incidentId,
            officerId: user?.id,
            queryReturn: incident.newQueryReturns + "\n",
            clear: false,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      let now = moment().format("MM/DD/YY HH:mm");
      let updatedQueryReturns = `------------- ${
        user?.badge ? user?.badge + ": " : ""
      }${user?.lastName}, ${user?.firstName} - ${now} -------------\n${
        incident.newQueryReturns
      }\n\n`;
      if (incident?.queryReturns) {
        updatedQueryReturns = updatedQueryReturns + incident.queryReturns;
      }
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incidentId ? incidentId : incident.uuid,
          property: "newQueryReturns",
          value: "",
        },
      });
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incidentId ? incidentId : incident.uuid,
          property: "queryReturns",
          value: updatedQueryReturns,
        },
      });
    } else {
      return null;
    }
  };

  const handleDateChange = (value, name, date) => {
    let parsedDate = parseDateFlexible(date);
    if (parsedDate?.isValid()) {
      const localTimeString = parsedDate.format("YYYY-MM-DD HH:mm:ss");
      const serverTimeFormat = parsedDate;
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incidentId ? incidentId : incident.uuid,
          property: name,
          value: localTimeString,
        },
      });
      updateCallTime({
        variables: {
          input: {
            incidentId: incidentId,
            originatorId: user?.id,
            callTime: serverTimeFormat,
          },
        },
      });
    }
  };

  const handleCheckbox = e => {
    let bool = e.target.checked;
    let name = e.target.value;
    let fieldToUpdate = stateToMutation[name];
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }

    switch (name) {
      case "rollCall":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: bool,
          },
        });
        updateRollCall({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: bool,
            },
          },
        });
        return null;

      case "callCancelled":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: bool,
          },
        });
        updateCallCancelled({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: bool,
            },
          },
        });
        return null;

      case "opiateBlocker":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: bool,
          },
        });
        updateOpiateBlocker({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: bool,
            },
          },
        });
        return null;

      case "mentalHealthRelated":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: bool,
          },
        });
        updateMentalHealth({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: bool,
            },
          },
        });
        return null;

      case "domv":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: bool,
          },
        });
        updateDomv({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: bool,
            },
          },
        });
        return null;

      case "cargoTheft":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: bool,
          },
        });
        updateCargoTheft({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: bool,
            },
          },
        });
        return null;

      default:
        alert("Name not found in handleChange function in InitialCallInfo.js");
        return null;
    }
  };

  const updateAddress = address => {
    incidentDispatch({
      type: UPDATE_INCIDENT,
      payload: {
        id: incidentId ? incidentId : incident.uuid,
        property: "address",
        value: address,
      },
    });
    activeIncidentDispatch({
      type: UPDATE_ACTIVE_INCIDENT_PROPERTY,
      payload: {
        incidentId: incident.id ? incident.id : incident.uuid,
        property: "address",
        value: address,
      },
    });
    if (address?.id) {
      updateIncidentAddress({
        variables: {
          input: {
            incidentId: incidentId ? incidentId : incident.uuid,
            addressId: address.id,
            originatorId: user?.id,
          },
        },
      });
    } else if (address === null) {
      updateIncidentAddress({
        variables: {
          input: {
            incidentId: incidentId ? incidentId : incident.uuid,
            addressId: "",
            originatorId: user?.id,
          },
        },
      });
    }
  };

  const stateToMutation = {
    classification: "classificationId",
    area: "areaId",
    team: "teamId",
    incidentPriority: "incidentPriorityId",
    callPriority: "callPriorityId",
    incidentOrigin: "incidentOriginId",
    rollCall: "rollCall",
    callCancelled: "callCancelled",
    opiateBlocker: "opiateBlocker",
    mentalHealthRelated: "mentalHealthRelated",
    domv: "domv",
    cargoTheft: "cargoTheft",
    selectedDrugTypes: "drugTypeIds",
    incidentFlags: "incidentFlagIds",
    callTime: "callTime",
    newNarrative: "newNarrative",
    newQueryReturns: "newQueryReturns",
    incidentNumber: "incidentNumber",
    stateWideEvents: "stateWideEventIds",
  };

  const {
    updateIncidentNumber,
    updateCallType,
    updateCallPriority,
    updateIncidentPriority,
    updateCallOrigin,
    updateCallTime,
    updateIncidentAddress,
    updateCallArea,
    updateCallTeam,
    updateRollCall,
    updateCallCancelled,
    updateStatewideEvents,
    updateDrugTypes,
    updateOpiateBlocker,
    updateMentalHealth,
    updateDomv,
    updateCargoTheft,
    setQueryReturns,
    addOrUpdateOfficerInIncident,
    throttledAddOrUpdateNicEntry,
    removeAddressAlert,
    throttledAddAddressAlertMutation,
    updateIncidentFlags,
  } = useContext(MutationContext);

  const handleAutoCompleteChange = (value, name) => {
    const fieldToUpdate = stateToMutation[name];
    switch (name) {
      case "classification": {
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: value,
          },
        });
        const classificationCommonCallType = value?.commonCallType;
        const secondaryCommonCallType =
          incident?.incidentDetail?.secondaryCallType?.commonCallType;
        const resolvedCommonCallType = pickPreferredCommonCallType(
          classificationCommonCallType,
          secondaryCommonCallType,
        );
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: "commonCallType",
            value: resolvedCommonCallType,
          },
        });
        activeIncidentDispatch({
          type: UPDATE_ACTIVE_INCIDENT_PROPERTY,
          payload: {
            incidentId: incident.id ? incident.id : incident.uuid,
            property: name,
            value: value,
          },
        });
        updateCallType({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: value?.id ? value?.id : "",
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return null;
      }

      case "area":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: value,
          },
        });
        updateCallArea({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: value?.id ? value.id : "",
            },
          },
        });
        return null;

      case "selectedDrugTypes": {
        let selectedDrugTypesIdString = parseObjectArrayIdString(value);
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: "drugTypes",
            value: value,
          },
        });
        updateDrugTypes({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: selectedDrugTypesIdString,
            },
          },
        });
        return null;
      }

      case "incidentFlags": {
        let incidentFlagIds = parseObjectArrayIdString(value);
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: "incidentFlags",
            value: value,
          },
        });
        updateIncidentFlags({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: incidentFlagIds,
            },
          },
        });
        return null;
      }

      default:
        alert("Name not found in handleChange function in InitialCallInfo.js");
        return null;
    }
  };

  const handleChange = event => {
    let name = event.target.name || null;
    let value = event.target.value;
    let fieldToUpdate = stateToMutation[name];

    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }

    switch (name) {
      case "incidentNumber":
        updateIncidentNumber({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: value,
            },
          },
        });
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: value,
          },
        });
        activeIncidentDispatch({
          type: UPDATE_ACTIVE_INCIDENT_PROPERTY,
          payload: {
            incidentId: incident.id ? incident.id : incident.uuid,
            property: name,
            value: value,
          },
        });
        return null;

      // case "classification":
      //   incidentDispatch({ type: UPDATE_INCIDENT, payload: { id: incident.id, uuid: incident.uuid, property: name, value: value } })
      //   activeIncidentDispatch({ type: UPDATE_ACTIVE_INCIDENT_PROPERTY, payload: { incidentId: incident.id ? incident.id : incident.uuid, property: name, value: value } })
      //   updateCallType({ variables: { input: { incidentId: incidentId ? incidentId : incident.uuid, originatorId: user?.id, [fieldToUpdate]: value.id } } })
      //   return null;

      case "callPriority":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: value,
          },
        });
        updateCallPriority({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: value.id,
            },
          },
        });
        return null;

      case "incidentPriority":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: value,
          },
        });
        updateIncidentPriority({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: value.id,
            },
          },
        });
        return null;

      case "incidentOrigin":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: value,
          },
        });
        updateCallOrigin({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: value.id,
            },
          },
        });
        return null;

      // case "area":
      //   incidentDispatch({ type: UPDATE_INCIDENT, payload: { id: incident.id, uuid: incident.uuid, property: name, value: value } })
      //   updateCallArea({ variables: { input: { incidentId: incidentId ? incidentId : incident.uuid, originatorId: user?.id, [fieldToUpdate]: value.id } } })
      //   return null;

      case "team":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: value,
          },
        });
        updateCallTeam({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: value.id,
            },
          },
        });
        return null;

      case "stateWideEvents": {
        let stateWideEventsIdString = parseObjectArrayIdString(value);
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: value,
          },
        });
        updateStatewideEvents({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: stateWideEventsIdString,
            },
          },
        });
        return null;
      }

      case "selectedDrugTypes": {
        let selectedDrugTypesIdString = parseObjectArrayIdString(value);
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: "drugTypes",
            value: value,
          },
        });
        updateDrugTypes({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: selectedDrugTypesIdString,
            },
          },
        });
        return null;
      }

      default:
        alert("Name not found in handleChange function in InitialCallInfo.js");
        return null;
    }
  };

  const handleLocalChange = event => {
    let name = event.target.name || null;
    let value = event.target.value;
    let fieldToUpdate = stateToMutation[name];
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }

    if (fieldToUpdate === undefined) {
      alert("Field not found");
    } else {
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incidentId ? incidentId : incident.uuid,
          property: name,
          value: value,
        },
      });
    }
  };

  // const handleRemovePerson = (pisId, numOfPersons) => {
  //   if (window.confirm('Are you sure you want to remove this person?')) {
  //     removePerson({ variables: { input: { personIncidentSpecificId: pisId, originatorId: user?.id } } })
  //     incidentDispatch({ type: REMOVE_PIS, payload: { incidentId: incidentId, pisId: pisId } })
  //     if (numOfPersons === 1) {
  //       handleAddPISToIncident()
  //     }
  //   }
  // }

  const handleCallLogModal = () => {
    setShowCallLogs(true);
  };

  const address = {
    addressName: incident.address ? incident.address.name : "",
    streetNumber: incident.address ? incident.address.streetNumber : "",
    streetName: incident.address ? incident.address.streetName : "",
    unit: incident.address ? incident.address.unit : "",
    city: incident.address ? incident.address.city : "",
    state: incident.address ? incident.address.state : "",
    zip: incident.address ? incident.address.zip : "",
    lat: incident.address ? incident.address.lat : "",
    lng: incident.address ? incident.address.lng : "",
    validAddressAlerts: incident.address
      ? incident.address.validAddressAlerts
      : "",
    id: incident.address ? incident.address.id : "",
  };

  useEffect(() => {
    if (incident?.personIncidentSpecifics.length === 0) {
      if (onlineState) {
        getPissForIncident && getPissForIncident();
      } else {
        const emptyPis = createEmptyPersonIncidentSpecific(
          null,
          null,
          null,
          user,
        );
        incidentDispatch({
          type: ADD_PIS,
          payload: { incidentId: incidentId, pis: emptyPis },
        });
      }
    }
  }, [
    ADD_PIS,
    incidentDispatch,
    incidentId,
    onlineState,
    incident?.personIncidentSpecifics.length,
    user,
    getPissForIncident,
  ]);

  const handleAddPISToIncident = useHandleAddPersonIncidentSpecific(incident);

  const handleAddClick = async () => {
    try {
      const { success, errors } = await handleAddPISToIncident();
      if (errors.length > 0) {
        alert(errors.join("\n\n"));
      } else if (success) {
        return;
      } else {
        alert("An error occurred while adding the person.");
      }
    } catch (error) {
      console.error(error);
      alert("An error occurred while adding the person in catch.");
    }
  };

  const handleRemovePerson = useHandleRemovePerson(incident);

  const handleRemoveClick = async pis => {
    try {
      const { success, cancelled, errors } = await handleRemovePerson(pis);
      if (cancelled) return;
      if (errors.length > 0) {
        alert(errors.join("\n\n"));
      } else if (!success) {
        alert("An error occurred while removing the person.");
      }
    } catch (error) {
      console.error(error);
      alert("An error occurred while removing the person in catch.");
    }
  };

  const throttledAddOrUpdateOfficerInIncident = throttleFunction(
    addOrUpdateOfficerInIncident,
  );

  const addEmptyOis = () => {
    if (
      incident?.officerIncidentSpecifics[
        incident?.officerIncidentSpecifics?.length - 1
      ]?.officer ||
      incident.officerIncidentSpecifics.length === 0
    ) {
      let oisUuid = handleUuid();
      let localOis = JSON.parse(JSON.stringify(emptyOis));
      localOis.id = oisUuid;
      localOis.uuid = oisUuid;
      localOis.incidentId = incident.id;
      incidentDispatch({
        type: ADD_EMPTY_OIS,
        payload: { incidentIndex: incidentIndex, ois: localOis },
      });
      activeIncidentDispatch({
        type: ADD_OIS_TO_INCIDENT,
        payload: {
          incidentId: incident?.id ?? incident?.uuid,
          incidentIndex: incidentIndex,
          ois: localOis,
        },
      });
      throttledAddOrUpdateOfficerInIncident({
        variables: {
          input: {
            incidentId: incident.id,
            originatorId: user?.id,
            officerIncidentSpecificId: oisUuid,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    } else {
      alert(
        "Please add an officer to the empty officer field below before adding a new person",
      );
    }
  };

  const handleAddNicEntry = () => {
    const nicUuid = handleUuid();
    let localNicEntry = JSON.parse(JSON.stringify(emptyNicEntry));
    localNicEntry.id = nicUuid;
    localNicEntry.uuid = nicUuid;
    localNicEntry.incidentId = incident.id;
    incidentDispatch({
      type: ADD_NIC,
      payload: { incidentIndex: incidentIndex, nicEntry: localNicEntry },
    });
    throttledAddOrUpdateNicEntry({
      variables: {
        input: {
          incidentId: incident.id,
          originatorId: user?.id,
          nicEntryId: nicUuid,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  const handleAddAddressAlert = alert => {
    throttledAddAddressAlertMutation({
      variables: {
        input: {
          addressAlertId: alert.id,
          officerId: user?.id,
          originatorId: user?.id,
          addressId: incident?.address?.id,
          alertTypeId: alert?.alertType?.id,
          notes: alert?.notes,
          expireAt: alert?.expireAt,
          incidentId: incident?.id ? incident?.id : null,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    let localAlerts = [...(incident?.address?.validAddressAlerts ?? [])];
    //This will check the incident alerts for an empty alert and replace it or push the new alert to the array
    const index = localAlerts.findIndex(a => a.id === alert.id);
    if (index >= 0) {
      localAlerts[index] = alert;
    } else {
      localAlerts.push(alert);
    }
    incidentDispatch({
      type: UPDATE_INCIDENT,
      payload: {
        incidentIndex: incidentIndex,
        accessor: "address",
        property: "validAddressAlerts",
        value: localAlerts,
      },
    });
  };

  const handleRemoveAddressAlert = alertId => {
    removeAddressAlert({
      variables: { input: { addressAlertId: alertId, originatorId: user?.id } },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    let localAlerts = incident?.address?.validAddressAlerts.filter(
      vaa => vaa.id.toString() !== alertId.toString(),
    );
    incidentDispatch({
      type: UPDATE_INCIDENT,
      payload: {
        incidentIndex: incidentIndex,
        accessor: "address",
        property: "validAddressAlerts",
        value: localAlerts,
      },
    });
  };

  return (
    // <ThemeProvider theme={cookies[`mp-userId-${user?.id}-night-mode`] === 'true' ? darkTheme : lightTheme}>
    <Paper
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        maxWidth: "100%",
        justifyContent: "flex-start",
        border: "1px solid black",
        alignItems: "center",
      }}
      autoComplete="off"
    >
      <CustomAutoComplete
        title={"Call\xa0Type"}
        options={classifications}
        addMargin={true}
        value={incident?.classification ? incident.classification : null}
        stateName={"classification"}
        updateFields={handleAutoCompleteChange}
        nameFormatter={false}
        minWidth={"150px"}
        userRoles={user?.roleNames}
        {...props}
      />
      <SingleDropDown
        userRoles={user?.roleNames}
        handleChange={handleChange}
        stateName={"callPriority"}
        value={incident?.callPriority}
        options={callPriorities}
        title={"Call\xa0Priority"}
        classStyle={classes.smallMatchMargin}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        {...props}
      />
      <SingleDropDown
        userRoles={user?.roleNames}
        handleChange={handleChange}
        stateName={"incidentPriority"}
        value={incident?.incidentPriority}
        options={incidentPriorities}
        title={"Incident\xa0Priority"}
        classStyle={classes.smallMatchMargin}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        {...props}
      />
      <SingleDropDown
        userRoles={user?.roleNames}
        handleChange={handleChange}
        stateName={"incidentOrigin"}
        value={incident?.incidentOrigin}
        options={incidentOrigins}
        title={"Call\xa0Origin"}
        classStyle={classes.smallMatchMargin}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        {...props}
      />
      <WrappedKeyboardDateTimePicker
        ampm={false}
        className={classes.smallMatchMargin}
        confirmFutureDate={true}
        clearable={true}
        dataCy="date-time-of-call-picker"
        disabled={memoizedLocked}
        format="MM/DD/YYYY HH:mm"
        label="Date & Time of Call"
        name="callTime"
        onBlur={() => setPausePolling(false)}
        onChange={handleDateChange}
        onFocus={() => setPausePolling(true)}
        showTodayButton={true}
        value={parseDateFlexible(incident?.callTime)}
      />
      <AddressWithAlertAndHmi
        userRoles={user?.roleNames}
        address={address}
        showHmi={true}
        showAlert={true}
        showPeople={true}
        addressInputLabel={"Location\xa0Of\xa0Call"}
        setAddress={updateAddress}
        validAlertName={"validAddressAlerts"}
        alertType={"address"}
        deleteAlert={handleRemoveAddressAlert}
        addAlert={handleAddAddressAlert}
        incidentId={incident?.id}
        onFocus={() => setPausePolling && setPausePolling(true)}
        onBlur={() => setPausePolling && setPausePolling(false)}
        showIntersection={true}
        handleIncidentOpening={handleIncidentOpening}
        handlePersonOpening={handlePersonOpening}
        incidentPeople={incidentPeople}
        {...props}
      />
      <CustomAutoComplete
        title={"Area"}
        options={areas}
        addMargin={true}
        value={incident?.area ? incident.area : null}
        stateName={"area"}
        updateFields={handleAutoCompleteChange}
        nameFormatter={false}
        minWidth={"150px"}
        userRoles={user?.roleNames}
        {...props}
      />
      <SingleDropDown
        userRoles={user?.roleNames}
        handleChange={handleChange}
        stateName={"team"}
        value={incident?.team}
        options={teams}
        title={"Team"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        {...props}
      />
      {/* Update disabled styling when roles are added */}
      <TextField
        data-cy="incident-number"
        name={"incidentNumber"}
        inputProps={{
          "data-cy": "incident-number-input",
          className: classes.disabled,
        }}
        id="incident-number-id"
        label="Incident Number"
        value={incident.incidentNumber}
        className={clsx(classes.matchMargin)}
        onChange={handleLocalChange}
        onBlur={handleChange}
        disabled={!isSuperUser(user?.roleNames)}
      />
      <FormGroup row>
        <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={!!incident.rollCall}
          onChange={handleCheckbox}
          stateName={"rollCall"}
          title={"Flag For Roll Call"}
          userRoles={user?.roleNames}
          {...props}
        />
        <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={!!incident.callCancelled}
          onChange={handleCheckbox}
          stateName={"callCancelled"}
          title={"Call Cancelled by Complainant"}
          userRoles={user?.roleNames}
          {...props}
        />
      </FormGroup>
      <FormControl
        style={{ marginTop: "auto", marginBottom: "auto" }}
        variant="standard"
        className={classes.formControl}
      >
        <Typography
          data-cy="created-by"
          style={{ border: "1px solid black", padding: "0px 5px" }}
        >
          Owner: {formatOfficerName(incident?.employee)}
        </Typography>
      </FormControl>
      <MultiDropDownObjects
        id={"stateWideEvents"}
        data-cy={"stateWideEvents"}
        title={"Incident\xa0Codes"}
        stateName={"stateWideEvents"}
        handleChange={handleChange}
        selectedValues={
          incident?.stateWideEvents
            ? stateWideEventsOptions.filter(option =>
                incident.stateWideEvents.some(swe => option?.id === swe?.id),
              )
            : []
        }
        options={stateWideEventsOptions}
        formStyle={classes.formControl}
        labelStyle={classes.multiInputLabel}
        selectStyle={null}
        chipsStyle={classes.chips}
        chipStyle={classes.chip}
        userRoles={user?.roleNames}
        {...props}
      />
      <SingleDropDown
        userRoles={null}
        disabled={true}
        handleChange={handleChange}
        stateName={"commonCallType"}
        value={incident?.commonCallType}
        options={commonCallTypes}
        title={"Common\xa0Call\xa0Type"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        {...props}
      />
      <CustomAutoComplete
        disableClearable={true}
        autoSelect={false}
        minWidth={"250px"}
        addMargin={true}
        title={"Drugs Involved"}
        groupBy={option => option.drugClassification.name}
        options={drugTypes.sort((a, b) => {
          if (a.drugClassification.position < b.drugClassification.position) {
            return -1;
          } else {
            return 1;
          }
        })}
        loading={false}
        error={""}
        multiple={true}
        limitTags={3}
        value={
          incident?.drugTypes
            ? drugTypes.filter(option =>
                incident.drugTypes.some(dt => option.id === dt.id),
              )
            : []
        }
        name={"name"}
        stateName={"selectedDrugTypes"}
        updateFields={handleAutoCompleteChange}
        id={"DrugsInvolvedObjects"}
        userRoles={user?.roleNames}
        setPausePolling={setPausePolling}
        {...props}
      />
      <PoldFields
        incident={incident}
        poldInstance={incident?.poldInstance}
        poldOptOut={incident?.ori?.poldOptOut}
        setPausePolling={setPausePolling}
        user={user}
        {...props}
      />
      <FormGroup
        row
        className={classes.matchMargin}
        style={{ border: "1px solid black" }}
      >
        <CheckboxWithLabel
          userRoles={user?.roleNames}
          formStyle={classes.formControl}
          checked={!!incident?.opiateBlocker}
          onChange={handleCheckbox}
          stateName={"opiateBlocker"}
          title={"Opiate\xa0Blocker"}
          {...props}
        />
        <CheckboxWithLabel
          userRoles={user?.roleNames}
          formStyle={classes.formControl}
          checked={!!incident?.mentalHealthRelated}
          onChange={handleCheckbox}
          stateName={"mentalHealthRelated"}
          title={"Mental\xa0Health"}
          {...props}
        />
        <CheckboxWithLabel
          userRoles={user?.roleNames}
          formStyle={classes.formControl}
          checked={!!incident?.domv}
          onChange={handleCheckbox}
          stateName={"domv"}
          title={"DOMV"}
          {...props}
        />
        <CheckboxWithLabel
          userRoles={user?.roleNames}
          formStyle={classes.formControl}
          checked={!!incident?.cargoTheft}
          onChange={handleCheckbox}
          stateName={"cargoTheft"}
          title={"Cargo\xa0Theft"}
          {...props}
        />
      </FormGroup>
      <CustomAutoComplete
        disableClearable={true}
        autoSelect={false}
        title={"Incident\xa0Flags"}
        options={incidentFlags?.sort((a, b) => {
          if (a.position < b.position) {
            return -1;
          } else {
            return 1;
          }
        })}
        addMargin={true}
        stateName={"incidentFlags"}
        value={
          incident?.incidentFlags
            ? incidentFlags?.filter(option =>
                incident.incidentFlags?.some(flag => option.id === flag.id),
              )
            : []
        }
        updateFields={handleAutoCompleteChange}
        multiple={true}
        nameFormatter={false}
        minWidth={"150px"}
        userRoles={user?.roleNames}
        {...props}
        limitTags={3}
        setPausePolling={setPausePolling}
        id={"incidentFlagsAutocomplete"}
      />
      {/* Need the print query returns */}
      <FormControl
        data-cy="narrative-full-width-form-control"
        fullWidth
        className={classes.formControl}
      >
        {hazardAlerts && (
          <TextField
            data-cy="alerts-textfield"
            inputProps={{
              "data-cy": "alerts-input",
              style: { color: "red", fontWeight: "bold" },
            }}
            InputLabelProps={{
              "data-cy": "alerts-input-label",
              style: { color: "black" },
            }}
            margin="dense"
            id="alerts-id"
            label="Alert:"
            value={hazardAlerts}
            name="addressAlertTextfield"
            multiline
            fullWidth
            variant="outlined"
            style={{ marginBottom: "2vh" }}
          />
        )}
        <NarrativeSegmentsContainer
          blocked={incident?.narrativesBlocked}
          roles={user?.roleNames}
          isOnIncident={memoizedIsOnIncident}
          user={user}
          onChange={handleLocalChange}
          value={incident?.newNarrative}
          disabled={memoizedLocked}
          createdIncident={createdIncident}
          incidentId={incident?.id ? incident.id : incident?.uuid}
          handleNarrativeSegmentSubscription={
            handleNarrativeSegmentSubscription
          }
          setPopper={setPopper}
          online={onlineState}
          subscriptionServer={subscriptionServer}
          nightMode={cookies[`mp-userId-${user?.id}-night-mode`] === "true"}
          incidentDispatch={incidentDispatch}
          narrativeSegments={incident?.narrativeSegments || []}
        />
      </FormControl>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <div className={classes.flexRow} style={{ alignItems: "center" }}>
          <button
            type="button"
            onClick={!memoizedLocked ? handleAddClick : null}
            disabled={memoizedLocked}
            style={{ background: "none", border: 0, padding: 0 }}
          >
            <img
              data-cy={"add-person-button-" + incident?.id}
              src={addButton}
              className={classes.pngButton}
              alt="Add Person"
            />
          </button>
          <Typography
            style={{ margin: "0 8px", fontWeight: "bold" }}
            data-cy="person-header"
          >
            People
          </Typography>
        </div>
        <div
          style={{ margin: "0 8px" }}
          id="person-container"
          data-cy="person-container"
        >
          {incident?.personIncidentSpecifics?.map((pis, index) => {
            return (
              <Person
                setPopper={setPopper}
                incidentIndex={incidentIndex}
                pisIndex={index}
                incidentInvolvements={incidentInvolvements}
                incidentId={incident?.id}
                incidentUuid={incident?.id ? incident.id : incident?.uuid}
                numOfPersons={incident?.personIncidentSpecifics.length}
                key={pis.id ? pis.id : pis.uuid}
                pis={pis}
                removePerson={handleRemoveClick}
                userRoles={user?.roleNames}
                setPausePolling={setPausePolling}
                handlePersonOpening={handlePersonOpening}
                handleIncidentOpening={handleIncidentOpening}
                showAliases={false}
                {...props}
              />
            );
          })}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <div className={classes.flexRow} style={{ alignItems: "center" }}>
          <button
            type="button"
            onClick={!memoizedLocked ? addEmptyOis : null}
            disabled={memoizedLocked}
            style={{ background: "none", border: 0, padding: 0 }}
          >
            <img
              data-cy={"add-officer-button-" + incident?.id}
              src={addButton}
              className={classes.pngButton}
              alt="Add Officer"
            />
          </button>
          <Typography
            style={{ margin: "0 8px", fontWeight: "bold" }}
            data-cy="responding-officer-header"
          >
            Responding&nbsp;Officers
          </Typography>
          {/* <Button variant="contained" className={""} onClick={addEmptyOis}>Add Officer&nbsp;<AddCircleOutlineIcon /></Button> */}
        </div>
        <div
          style={{ width: "100%", margin: "0 8px" }}
          id="responding-officer-container"
          data-cy="responding-officer-container"
        >
          {incident?.officerIncidentSpecifics?.map((ois, index) => {
            return (
              <RespondingOfficer
                incidentIndex={incidentIndex}
                incidentId={incident?.id}
                incidentReviewId={incident?.incidentReview?.id}
                incidentDetailId={incident?.incidentDetail?.id}
                oisIndex={index}
                numOfOfficers={incident?.officerIncidentSpecifics.length}
                key={ois?.id ? ois?.id : ois?.uuid}
                ois={ois}
                oiss={incident?.officerIncidentSpecifics}
                addEmptyOis={addEmptyOis}
                userRoles={userRoles}
                isOnIncident={memoizedIsOnIncident}
                {...props}
              />
            );
          })}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <div className={classes.flexRow} style={{ alignItems: "center" }}>
          <button
            type="button"
            onClick={!memoizedLocked ? handleAddNicEntry : null}
            disabled={memoizedLocked}
            style={{ background: "none", border: 0, padding: 0 }}
          >
            <img
              data-cy={"add-nic-entry-button-" + incident?.id}
              src={addButton}
              className={classes.pngButton}
              alt="Add NIC Entry"
            />
          </button>
          <Typography
            style={{ margin: "0 8px", fontWeight: "bold" }}
            data-cy="mri-header"
          >
            MRIs
          </Typography>
          {/* <Button variant="contained" className={""} onClick={handleAddNicEntry}>Add MRI&nbsp;<AddCircleOutlineIcon /></Button> */}
        </div>
        <div
          style={{ width: "100%", margin: "0 8px" }}
          id="MRI-container"
          data-cy="MRI-container"
        >
          {incident?.nicEntries?.map((nic, index) => {
            return (
              <MRI
                incidentIndex={incidentIndex}
                nicEntryIndex={index}
                key={nic.id}
                nic={nic}
                numOfNicEntries={incident?.nicEntries.length}
                incidentId={incidentId}
                userId={user?.id}
                handleAddNicEntry={handleAddNicEntry}
                userRoles={userRoles}
                {...props}
              />
            );
          })}
        </div>
      </div>

      <FormControl fullWidth className={classes.formControl}>
        <FormLabel component="label">{"New\xa0Query\xa0Returns"}</FormLabel>
        <TextareaAutosize
          onChange={handleLocalChange}
          data-cy="newQueryReturns"
          className={
            cookies[`mp-userId-${user?.id}-night-mode`] === "true"
              ? classes.textAreaDark
              : classes.textAreaLight
          }
          margin="dense"
          id="newQueryReturns-id"
          value={incident?.newQueryReturns || ""}
          minRows={2}
          name="newQueryReturns"
          disabled={memoizedLocked}
        />
        <Box display="flex" flexDirection="row">
          <Button
            data-cy="newQueryReturns-submit-button"
            style={{
              width: "fit-content",
              marginTop: "1vh",
              marginBottom: "3vh",
              marginRight: "2vw",
            }}
            variant="contained"
            onClick={!memoizedLocked ? () => handleSetQueryReturn() : null}
          >
            Submit
          </Button>
          <Button
            data-cy="newQueryReturns-cancel-button"
            style={{
              width: "fit-content",
              marginTop: "1vh",
              marginBottom: "3vh",
            }}
            variant="contained"
            onClick={() =>
              incidentDispatch({
                type: UPDATE_INCIDENT,
                payload: {
                  id: incidentId ? incidentId : incident.uuid,
                  property: "newQueryReturns",
                  value: "",
                },
              })
            }
          >
            Cancel
          </Button>
        </Box>
        <Box display="flex" flexDirection="row" alignItems="center">
          <FormLabel component="label">{"Query\xa0Returns"}</FormLabel>
          <IconButton
            edge="start"
            onClick={() =>
              queryReturnHeight === "30vh"
                ? setQueryReturnHeight("fit-content")
                : setQueryReturnHeight("30vh")
            }
            style={{ margin: 0 }}
          >
            {queryReturnHeight === "30vh" ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>
        <TextareaAutosize
          disabled={false}
          style={{ maxHeight: queryReturnHeight, overflow: "auto" }}
          data-cy="queryReturns"
          //className={cookies[`mp-userId-${user?.id}-night-mode`] === 'true' ? classes.textAreaDark : classes.textAreaLight}
          // className={queryReturnHeight === 'fit-content' ? 'expanded' : 'not-expanded'}
          className={
            cookies[`mp-userId-${user?.id}-night-mode`] === "true"
              ? classes.textAreaDark
              : classes.textAreaLight
          }
          margin="dense"
          id="queryReturns-id"
          value={incident?.queryReturns || ""}
          minRows={4}
          name="queryReturns"
        />
      </FormControl>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-around"
        style={{ width: "100%" }}
      >
        <Button
          disabled={
            !onlineState && import.meta.env.VITE_FORCE_ONLINE !== "true"
          }
          style={{
            width: "fit-content",
            marginTop: "1vh",
            marginBottom: "3vh",
            marginRight: "2vw",
          }}
          variant="contained"
          onClick={() => handleCallLogModal(incidentId)}
        >
          Display Call Log
        </Button>
        <Button
          style={{
            width: "fit-content",
            marginTop: "1vh",
            marginBottom: "3vh",
            marginRight: "2vw",
          }}
          variant="contained"
          onClick={!memoizedLocked ? () => setShowLinkIncident(true) : null}
        >
          Link Incidents
        </Button>
        <Button
          disabled={
            !onlineState && import.meta.env.VITE_FORCE_ONLINE !== "true"
          }
          style={{
            width: "fit-content",
            marginTop: "1vh",
            marginBottom: "3vh",
            marginRight: "2vw",
          }}
          variant="contained"
          onClick={() => setShowLinkedIncidents(true)}
        >
          Show Linked
        </Button>
        {/* <Button disabled={!onlineState && import.meta.env.VITE_FORCE_ONLINE !== 'true'} style={{ width: "fit-content", marginTop: '1vh', marginBottom: '3vh', marginRight: "2vw" }} variant="contained" onClick={() => handleReactModal()}>Redact</Button> */}
      </Box>
      <CallLogs
        showCallLogs={showCallLogs}
        setShowCallLogs={setShowCallLogs}
        modalStyle={classes.modal}
        incidentId={incident?.id ? incident.id : incident.uuid}
        incidentNumber={incident?.incidentNumber}
        userId={user?.id}
      />
      <LinkIncident
        {...props}
        userRoles={userRoles}
        showLinkIncident={showLinkIncident}
        setShowLinkIncident={setShowLinkIncident}
        incidentId={incident?.id ? incident.id : incident.uuid}
        modalStyle={classes.modal}
        paperStyle={classes.paper}
        formStyle={classes.formControl}
        incidentIndex={incidentIndex}
      />
      <LinkedIncidents
        handleIncidentOpening={handleIncidentOpening}
        showLinkedIncidents={showLinkedIncidents}
        setShowLinkedIncidents={setShowLinkedIncidents}
        formStyle={classes.formControl}
        fabStyle={classes.fabButton}
        modalStyle={classes.modal}
        paperStyle={classes.paper}
        incidentId={incidentId}
        incidentIndex={incidentIndex}
        linkedIncidents={incident?.linkedIncidents || []}
        relatedIncidents={incident?.relatedIncidents || []}
        userRoles={userRoles}
        {...props}
      />
    </Paper>
    // </ThemeProvider>
  );
};

// InitialCallInformation.propTypes = {
//   setup: PropTypes.object
// };

export default InitialCallInformation;
